/**
 * reachability: entry-point — when the launcher may swap its still face for
 * the looping clip. Pure so the gate and every fallback are unit tested.
 */

export type SarahAvatarVideoEnv = {
  /** SARAH_AVATAR_VIDEO_ENABLED. */
  enabled: boolean
  /** matchMedia('(prefers-reduced-motion: reduce)').matches */
  reducedMotion: boolean
  /** navigator.connection.saveData */
  saveData: boolean
  /** The clip failed to load or play once this page view. */
  failed: boolean
}

export function sarahAvatarVideoAllowed(env: SarahAvatarVideoEnv): boolean {
  return env.enabled && !env.reducedMotion && !env.saveData && !env.failed
}

/** Read the browser half of the env. Safe on the server (returns no motion). */
export function readSarahAvatarVideoEnv(): Pick<SarahAvatarVideoEnv, 'reducedMotion' | 'saveData'> {
  if (typeof window === 'undefined') return { reducedMotion: true, saveData: true }
  const reducedMotion =
    typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  const conn = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection
  return { reducedMotion, saveData: conn?.saveData === true }
}

/** The gesture plays this long after the page goes idle (first play only). */
export const SARAH_GESTURE_FIRST_DELAY_MS = 1500
/** The one replay comes this long after the gesture last ended. */
export const SARAH_GESTURE_REPLAY_DELAY_MS = 45_000
/** First play plus one replay, per browser session. */
export const SARAH_GESTURE_MAX_PLAYS = 2

/**
 * How long to wait before the next gesture, or null when the session has had
 * its plays. `plays` is how many have started this session.
 */
export function sarahGestureDelay(plays: number): number | null {
  if (plays <= 0) return SARAH_GESTURE_FIRST_DELAY_MS
  if (plays < SARAH_GESTURE_MAX_PLAYS) return SARAH_GESTURE_REPLAY_DELAY_MS
  return null
}

const GESTURE_PLAYS_KEY = 'sarah:gesture-plays'
// Used when sessionStorage throws (private mode, blocked storage).
let memoryPlays = 0

type SessionStore = Pick<Storage, 'getItem' | 'setItem'>

function sessionStore(): SessionStore | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage
  } catch {
    return null
  }
}

export function readSarahGesturePlays(store: SessionStore | null = sessionStore()): number {
  try {
    const n = Number(store?.getItem(GESTURE_PLAYS_KEY))
    return Math.max(memoryPlays, Number.isFinite(n) ? n : 0)
  } catch {
    return memoryPlays
  }
}

export function recordSarahGesturePlay(store: SessionStore | null = sessionStore()): number {
  const next = readSarahGesturePlays(store) + 1
  memoryPlays = next
  try {
    store?.setItem(GESTURE_PLAYS_KEY, String(next))
  } catch {
    // memoryPlays still caps this page view.
  }
  return next
}

/** Test hook: forget the in-memory count. */
export function resetSarahGesturePlaysForTest(): void {
  memoryPlays = 0
}
